import axios from "axios";
import useAuth from "@/modules/oauth/composables/useAuth";
import { ToastMixin } from "@/tools/ToastMixin";
import applyCaseMiddleware from "axios-case-converter";
import checkErrorsApiResponse from "@/tools/checkErrorsApiResponse";
import { AuthService } from "@/modules/oauth/services/AuthService";
import { getModuleApiConfig } from "@/modules/api/config/moduleApiConfig";

const toast = ToastMixin("error");
const hdaApiConfig = getModuleApiConfig("hda");

// --- 401 refresh-and-retry state (module-level singletons) ---
let isRefreshing = false;

interface QueueItem {
  resolve: (token: string) => void;
  reject: (reason: unknown) => void;
}
const failedQueue: QueueItem[] = [];

function processQueue(error: unknown, token: string | null): void {
  failedQueue.forEach((item) => {
    if (error) {
      item.reject(error);
    } else {
      item.resolve(token as string);
    }
  });
  failedQueue.length = 0;
}

const hdaRest = applyCaseMiddleware(
  axios.create({
    baseURL: hdaApiConfig.baseUrl,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-api-key": hdaApiConfig.apiKey,
    },
    timeout: 30000,
  })
);

hdaRest.interceptors.request.use(
  (config) => {
    const { accessToken } = useAuth();
    if (accessToken.value) {
      config.headers.Authorization = "Bearer " + accessToken.value;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

hdaRest.interceptors.request.use((config) => {
  const contentType = config.headers?.["Content-Type"];
  if (
    typeof config.data === "string" &&
    typeof contentType === "string" &&
    contentType.includes("application/json")
  ) {
    try {
      config.data = JSON.parse(config.data);
    } catch {
      // keep original payload if it is not valid JSON
    }
  }
  return config;
});

hdaRest.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as { headers: Record<string, string>; _retry?: boolean };

    if (error.response?.status !== 401) {
      throw error;
    }

    if (originalRequest._retry) {
      // The retried request also got 401; stop retrying and close session.
      const { revokeSession } = useAuth();
      revokeSession();
      globalThis.location.href = globalThis.location.origin;
      throw error;
    }

    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers["Authorization"] = "Bearer " + token;
        return hdaRest.request(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const { refreshToken, setAccessToken, setRefreshToken, setInitDate, revokeSession } = useAuth();

    try {
      const authService = new AuthService();
      const { data } = await authService.refreshToken(refreshToken.value ?? "");

      setAccessToken(data.accessToken);
      setRefreshToken(data.refreshToken);
      setInitDate();

      processQueue(null, data.accessToken);
      originalRequest.headers["Authorization"] = "Bearer " + data.accessToken;
      return hdaRest.request(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      revokeSession();
      globalThis.location.href = globalThis.location.origin;
      throw refreshError;
    } finally {
      isRefreshing = false;
    }
  }
);

hdaRest.interceptors.response.use(
  (response) => response,
  async (error) => {
    const errorData = checkErrorsApiResponse(error);
    if (errorData.icon !== "success") {
      const toastOptions: Record<string, unknown> = {
        icon: errorData.icon,
        title: errorData.title,
      };
      if (errorData.text) {
        toastOptions.text = errorData.text;
      }
      await toast.fire(toastOptions);
    }
    throw error;
  }
);

export default hdaRest;
